import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  ChangeEvent, useEffect, useState
} from 'react';
import { debounce, getDate } from '@shared/utils';
import { t as i18n } from 'i18next';
import { useTranslation } from 'react-i18next';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import {
  Flex, Input, Text
} from '@huspy/briks-web';
import { Search } from '@huspy/briks-icons';
import { ClientsRoute } from '@modules/clients/presentation/routes';
import FilterButton from '@components/v2/FilterButton';
import DatePicker from '@components/v2/DateRange';
import { format } from 'date-fns';

const SEGMENTED_CONTROL_DATA = [
  {
    label: i18n('clients.filterBy.labels.allTime'),
    value: 'all',
  },
  {
    label: i18n('clients.filterBy.labels.sevenDays'),
    value: '7d',
  }
];

const DATE_ISO_FORMAT = 'yyyy-MM-dd';

const formatDateRangeForPicker = (dateRange: [Date | null, Date | null]) => dateRange
  .filter((v) => !!v)
  .map((el) => format(el, DATE_ISO_FORMAT));

const getSegmentedControlValue = (sdr?: string, edr?: string) => {
  if (!sdr || !edr) return 'all';
  const startDate = new Date(sdr);
  const endDate = new Date(edr);

  const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
  const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

  if (daysDifference === 7) {
    return '7d';
  }
  if (daysDifference === 30) {
    return '30d';
  }

  return null;
};

const getSegmentedControlDates = (value: string): [Date, Date] => {
  const currentDate = new Date();

  const sevenDaysAgo = new Date(currentDate);
  const daysToSubtract = value === '7d' ? 7 : 30;
  sevenDaysAgo.setDate(currentDate.getDate() - daysToSubtract);

  return [sevenDaysAgo, currentDate];
};

const ControlPanel = () => {
  const { edr: endDateRange, sdr: startDateRange, q } = ClientsRoute.useSearch();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startDateRange && startDateRange.length > 0 ? new Date(startDateRange) : null,
    endDateRange && endDateRange.length > 0 ? new Date(endDateRange) : null
  ]);
  const navigate = useNavigate({ from: ClientsRoute.fullPath });
  const { hash } = useLocation();
  const { t } = useTranslation();

  const debouncedSearch = debounce((value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...(value.length > 0 ? { q: value } : { q: undefined }),
      }),
    });
  }, 500);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    trackAmplitudeEvent('Clients Search Client');
    debouncedSearch(e.target.value);
  };

  const handleSegmentedControlChange = (value: string) => {
    setDateRange(value === 'all' ? [null, null] : getSegmentedControlDates(value));
  };

  useEffect(() => {
    if (dateRange.every(Boolean)) {
      navigate({
        to: ClientsRoute.to,
        search: (prev) => ({
          ...prev,
          sdr: getDate(dateRange[0]),
          edr: getDate(dateRange[1]),
        }),
        hash,
      });
    } else if (!dateRange.every(Boolean)) {
      navigate({ to: ClientsRoute.to, hash, search: ({ sdr: _sdr, edr: _edr, ...prev }) => (prev) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  return (
    <Flex direction='column'>
      <Flex gap={ 6 } justify='flex-start' align='center'>
        <Flex w='221px'>
          <Input
            placeholder={ t('opportunity.caseList.v2.search') }
            bg='bg-secondary'
            borderColor='border-secondary'
            backdropFilter='blur(32px);'
            icon={ <Search width={ 24 } height={ 24 } color='#6B7280' /> }
            iconPosition='left'
            defaultValue={ q }
            w='100%'
            onChange={ handleSearch }
            spellCheck={ false }
            size='md'
            css={ { paddingLeft: '12' } }
            data-test='search-client-input'
          />
        </Flex>

        <Flex gap={ 2 }>
          {
            SEGMENTED_CONTROL_DATA.map((controlItem) => (
              <FilterButton
                key={ controlItem.value }
                isActive={ getSegmentedControlValue(startDateRange, endDateRange) === controlItem.value }
                dataTest={ controlItem.value }
                handleClick={ () => handleSegmentedControlChange(controlItem.value) }
              >
                <Text size='md'>{controlItem.label}</Text>
              </FilterButton>
            ))
          }

          <DatePicker
            cursor='pointer'
            onValueChange={ (e) => {
              setDateRange([
                e.valueAsString[0] ? new Date(e.valueAsString[0]) : null,
                e.valueAsString[1] ? new Date(e.valueAsString[1]) : null
              ]);

              if (!e.valueAsString[0] && !e.valueAsString[1]) {
                setDateRange([null, null]);
              }
            } }
            value={ formatDateRangeForPicker(dateRange) }
            max={ format(new Date(), 'yyyy-MM-dd') }
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ControlPanel;
